import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CurrentUserDto } from '@nova/shared';

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: CurrentUserDto | null;
  setSession: (tokens: { accessToken: string; refreshToken: string }, user: CurrentUserDto) => void;
  setUser: (user: CurrentUserDto) => void;
  setTokens: (tokens: { accessToken: string; refreshToken: string }) => void;
  clear: () => void;
  /** SEC-01 — screen-level check used by route guards and to hide menu items. */
  can: (...permissions: string[]) => boolean;
  canAny: (...permissions: string[]) => boolean;
  /**
   * Patient-phone privacy — a service provider (a user with a provider profile)
   * does not see a patient's mobile number unless one of their roles grants it,
   * and ADMIN always does. The API strips the value; this decides whether the UI
   * shows the field at all.
   */
  canViewPatientPhone: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,

      setSession: (tokens, user) =>
        set({ accessToken: tokens.accessToken, refreshToken: tokens.refreshToken, user }),
      setUser: (user) => set({ user }),
      setTokens: (tokens) => set({ accessToken: tokens.accessToken, refreshToken: tokens.refreshToken }),
      clear: () => set({ accessToken: null, refreshToken: null, user: null }),

      can: (...permissions) => {
        const user = get().user;
        if (!user) return false;
        // Mirrors PermissionsGuard: the admin role passes everything.
        if (user.roleNames.includes('ADMIN')) return true;
        return permissions.every((p) => user.permissions.includes(p));
      },

      canAny: (...permissions) => {
        const user = get().user;
        if (!user) return false;
        if (user.roleNames.includes('ADMIN')) return true;
        return permissions.some((p) => user.permissions.includes(p));
      },

      canViewPatientPhone: () => {
        const user = get().user;
        if (!user) return false;
        if (user.roleNames.includes('ADMIN')) return true;
        // Only a service provider is gated; the front desk and accounting are not.
        if (user.providerId === null) return true;
        return user.canViewPatientPhone ?? false;
      },
    }),
    {
      name: 'nova-auth',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
      }),
    },
  ),
);

/** Non-hook access, for the axios interceptor. */
export const authStore = useAuthStore;

/** React binding for the patient-phone privacy rule. */
export function useCanViewPatientPhone(): boolean {
  return useAuthStore((state) => state.canViewPatientPhone());
}
